export const LEGAL_PARAMS_TABLE = 'legal_params';

export const LEGAL_PARAM_KEYS = [
  'ss_tope_max_mensual',
  'ss_tope_max_diario',
  'ss_tope_max_semanal',
  'ss_empresa_base_maxima',
  'ss_tasa_temporal',
  'ss_tasa_indefinido',
  'irpf_min_artistas',
  'irpf_autonomos',
  'irpf_nuevos_autonomos',
  'smi_mensual',
  'plus_flexibilidad',
  'dieta_completa_pernocta',
  'dieta_completa_sin_pernocta',
  'dieta_media_comida',
  'dieta_media_cena',
  'gastos_bolsillo',
  'kilometraje',
] as const;

export function applyLegalParams(
  projectParams: Record<string, number>,
  legalValues: Record<string, number>,
): Record<string, number> {
  const result = { ...projectParams };
  for (const key of LEGAL_PARAM_KEYS) {
    delete result[key];
    const value = legalValues[key];
    if (Number.isFinite(value)) result[key] = value;
  }
  return result;
}

export type LegalParamUnit = 'eur_mes' | 'eur_dia' | 'eur_semana' | 'eur_km' | 'tasa' | string;

export interface LegalParam {
  key: string;
  value: number;
  unit: LegalParamUnit;
  label: string;
  category: string;
  position: number;
  valid_from: string | null;
  source: string | null;
  updated_at: string | null;
}

const UNIT_SUFFIX: Record<string, string> = {
  eur_mes: '€/mes',
  eur_dia: '€/día',
  eur_semana: '€/semana',
  eur_km: '€/km',
};

export function readLegalParams(rows: unknown): LegalParam[] {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row) => {
      const r = row as Record<string, unknown>;
      return {
        key: String(r.key ?? ''),
        value: Number(r.value),
        unit: String(r.unit ?? ''),
        label: String(r.label ?? r.key ?? ''),
        category: String(r.category ?? ''),
        position: Number(r.position) || 0,
        valid_from: r.valid_from ? String(r.valid_from) : null,
        source: r.source ? String(r.source) : null,
        updated_at: r.updated_at ? String(r.updated_at) : null,
      };
    })
    .filter((p) => p.key && Number.isFinite(p.value))
    .sort((a, b) => a.position - b.position);
}

export function legalParamValues(params: LegalParam[]): Record<string, number> {
  const values: Record<string, number> = {};
  for (const p of params) values[p.key] = p.value;
  return values;
}

export function groupLegalParams(params: LegalParam[]): Array<{ category: string; params: LegalParam[] }> {
  const groups: Array<{ category: string; params: LegalParam[] }> = [];
  for (const p of params) {
    const group = groups.find((g) => g.category === p.category);
    if (group) group.params.push(p);
    else groups.push({ category: p.category, params: [p] });
  }
  return groups;
}

export function formatLegalValue(unit: LegalParamUnit, value: number): string {
  if (unit === 'tasa') {
    return `${(value * 100).toLocaleString('es-ES', { maximumFractionDigits: 2 })} %`;
  }
  const amount = value.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const suffix = UNIT_SUFFIX[unit];
  return suffix ? `${amount} ${suffix}` : amount;
}

export function toLegalInput(unit: LegalParamUnit, value: number): string {
  const shown = unit === 'tasa' ? Math.round(value * 100 * 10000) / 10000 : value;
  return String(shown).replace('.', ',');
}

export function parseLegalInput(unit: LegalParamUnit, raw: string): number | null {
  const cleaned = raw.replace(/[€%\s]/g, '').replace(/\.(?=\d{3}(\D|$))/g, '').replace(',', '.');
  if (!cleaned) return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n) || n < 0) return null;
  return unit === 'tasa' ? Math.round((n / 100) * 1e6) / 1e6 : n;
}
